import { ApiError } from "./ApiError";

/**
 * Mutator tunggal untuk seluruh client generated orval di paket ini.
 *
 * Berbeda dari `authFetch` milik apps/web dan itu disengaja: permukaan yang dipanggil di
 * sini seluruhnya `security: []`, jadi TIDAK ADA jalur token — tanpa refresh, tanpa retry
 * 401, tanpa `credentials: "include"`. Menambahkannya bukan sekadar kode mati: ia membuat
 * pembaca berikutnya mengira template punya sesi, lalu menulis hook yang bergantung padanya.
 *
 * Konteks penyewa TIDAK dikirim template dan memang tidak boleh: gobackend meresolusinya
 * dari header `Origin`/`Host` permintaan (docs/pedoman/06-tenancy.md T-08). Template desa A
 * yang dilayani di domain desa A otomatis berada di penyewa A; tidak ada parameter
 * organisasi yang perlu — atau boleh — disodorkan dari sisi klien.
 *
 * # Kenapa default-nya `/api/v1` dan BUKAN sebuah host absolut
 *
 * Nilai ini di-*bake* ke dalam bundel saat `vite build`, lalu bundel itu di-zip dan diunggah
 * untuk dipakai desa mana pun. Default absolut karena itu adalah bug yang menunggu: default
 * lama `http://localhost:3000` membuat setiap template yang dibangun tanpa `.env` menembak
 * mesin PENGUNJUNG, dan gagal dengan "failed to load response" — bukan galat backend, jadi
 * tidak ada jejaknya di log server.
 *
 * `/api/v1` relatif menghindari itu sekaligus MENJADI syarat tenancy di atas: nginx tiap
 * domain desa meneruskan `location /api/v1/` ke gobackend
 * (apps/tatatemplate/nginx-config-generator/templates/domain.conf.tmpl:54-55), sehingga
 * permintaannya se-origin dan `Origin`/`Host` tetap domain desa itu. Base URL absolut ke satu
 * host bersama akan mengirim SELURUH desa ke penyewa yang sama.
 *
 * `VITE_API_BASE_URL` tetap dihormati untuk dev lokal (mis. `http://localhost:3000`).
 */
export const BASE_URL: string = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export const publicFetch = async <T>(
  url: string,
  init: RequestInit & { method?: string } = {},
): Promise<T> => {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  // Dedup retry level-jaringan untuk create (middleware idempotency di gobackend).
  // Kontrak tidak mendeklarasikan header ini sebagai parameter, jadi orval tidak akan
  // pernah mengirimkannya sendiri — di sinilah satu-satunya tempat ia bisa dipasang.
  if (method === "POST" && !headers.has("Idempotency-Key")) {
    headers.set("Idempotency-Key", crypto.randomUUID());
  }

  // Boundary multipart hanya benar bila browser yang mengisi Content-Type sendiri.
  if (init.body instanceof FormData) headers.delete("Content-Type");
  else if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(`${BASE_URL}${url}`, { ...init, method, headers });

  // Response non-JSON: `GET /site/{organizationId}` memulangkan text/html, dan kontraknya
  // menyatakan itu. Mengurainya sebagai JSON akan melempar SyntaxError yang menyesatkan.
  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) {
    const text = await response.text();
    if (!response.ok) throw new ApiError(response.status, text);
    return text as T;
  }

  const body = await response.json();
  if (!response.ok) throw new ApiError(response.status, body);

  // Envelope kegagalan bisa datang dengan HTTP 200 (ResponseJSON warisan Hono
  // meng-hardcode 200 untuk sebagian error, dan gobackend mempertahankan bentuknya).
  // Tanpa cek ini, react-query menandai kegagalan sebagai sukses dan UI menampilkan
  // keadaan kosong alih-alih galat.
  if (isFailureBody(body)) throw new ApiError(response.status, body);

  return body as T;
};

function isFailureBody(body: unknown): boolean {
  if (typeof body !== "object" || body === null) return false;
  const record = body as Record<string, unknown>;
  return record.status === "failed" || record.success === false;
}
